import { useMutation, useQuery, useQueryClient } from "react-query";

import { LocationService } from "./location.service";
import toast from "react-hot-toast";

// GET List
const useGetList = (params: TParams & { charge_point_id?: string }) => {
  return useQuery(
    ["listLocation", params],
    async () => {
      const { data } = await LocationService.getListLocation({
        ...params,
        page: params.page,
        charge_point_id: params.charge_point_id,
      });
      return data;
    },
    { keepPreviousData: true, retryOnMount: false }
  );
};

const useLocationOccupancyReports = (
  params: {
    from_date: string;
    to_date: string;
    type?: "location-occupancy-reports";
  },
) => {
  return useQuery(
    ["locationOccupancyReports", params],
    async () => {
      const { data } = await LocationService.getLocationOccupancyReports(params);
      return data;
    },
    {
      keepPreviousData: true,
      enabled: Boolean(params.from_date) && Boolean(params.to_date),
    },
  );
};

const useEnableSubscriptionPlans = (cb?: (data: any) => void) => {
  const queryClient = useQueryClient();
  return useMutation(
    async (createItem: any) => {
      const data = await LocationService.enableSubscriptionPlans(createItem);
      return data;
    },
    {
      onSuccess: (data) => {
        cb?.(data);
        if (data.ok) {
          toast.success("Create successfully!");
          queryClient.invalidateQueries(["list_location"]);
        } else {
          toast.error(data?.msg);
        }
      },
      onError(error, _variables, _context) {
        console.log(error);
      },
    }
  );
};

const useCreateLocation = (callback?: (data: any) => void) => {
  const queryClient = useQueryClient();
  return useMutation(
    async (createdItem: any) => {
      const data = await LocationService.createLocation(createdItem);
      return data;
    },
    {
      onSuccess: (data) => {
        callback?.(data);
        if (data.ok) {
          toast.success("Create Location Success!");
          queryClient.invalidateQueries(["listLocation"]);
        } else {
          toast.error(data?.msg);
        }
      },
      onError(error: any) {
        toast.error(error?.msg);
        return error;
      },
    }
  );
};

const useUpdateLocation = (callback?: (data: any) => void) => {
  const queryClient = useQueryClient();
  return useMutation(
    async (payload: any) => {
      const data = await LocationService.updateLocation(payload);
      return data;
    },
    {
      onSuccess: (data) => {
        callback?.(data);
        if (data.ok) {
          toast.success("Update Location Success!");
          queryClient.invalidateQueries(["listLocation"]);
        } else {
          toast.error(data?.msg);
        }
      },
      onError(error: any) {
        toast.error(error?.msg);
        return error;
      },
    }
  );
};

const useDeleteLocation = (callback?: (data: any) => void) => {
  const queryClient = useQueryClient();
  return useMutation(
    async (id: string) => {
      const data = await LocationService.deleteLocation(id);
      return data;
    },
    {
      onSuccess: (data) => {
        callback?.(data);
        if (data.ok) {
          toast.success("Delete Location Success!");
          queryClient.invalidateQueries(["listLocation"]);
        } else {
          toast.error(data?.msg);
        }
      },
      onError(error: any) {
        toast.error(error?.msg);
        return error;
      },
    }
  );
};

// GET Detail
const useGetDetail = (id?: string) => {
  return useQuery(
    ["getDetailLocation", id],
    async () => {
      const data = await LocationService.getDetailLocation(id as string);
      return data;
    },
    { enabled: Boolean(id) }
  );
};

export const useEnableQueue = () =>
  useMutation({
    mutationFn: async (dto: { id: string; is_active: boolean }) => {
      const data = await LocationService.enableQueue(dto);
      return data;
    },
  });

export {
  useCreateLocation,
  useDeleteLocation,
  useEnableSubscriptionPlans,
  useGetList,
  useLocationOccupancyReports,
  useUpdateLocation,
  useGetDetail,
};
